/* eslint-disable */
import { getAPI } from "@/axios-api";

/**
 * Fetch a Phishing campaign by ID
 * @param {string|number} campaignId - The ID of the campaign to fetch
 * @returns {Promise<Object|null>} The campaign data or null if not found
 */
export async function fetchPhishingCampaignById(campaignId) {
  try {
    const response = await getAPI.get(`phishing/campaign/${campaignId}/`);
    if (response.status === 200) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error(`Error fetching Phishing campaign with ID ${campaignId}:`, error);
    return null;
  }
}

/**
 * Fetch campaign records for a specific Phishing campaign
 * @param {string|number} campaignId - The ID of the campaign
 * @returns {Promise<Array|null>} The campaign records or null if not found
 */
export async function fetchPhishingCampaignRecords(campaignId) {
  try {
    const response = await getAPI.get(`smshing/campaign/${campaignId}/records/`);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error(`Error fetching Phishing campaign records for ID ${campaignId}:`, error);
    return null;
  }
}

/**
 * Fetch campaign analytics for a specific Phishing campaign
 * @param {string|number} campaignId - The ID of the campaign
 * @returns {Promise<Object|null>} The campaign analytics or null if not found
 */
export async function fetchPhishingCampaignAnalytics(campaignId) {
  try {
    const response = await getAPI.get(`smshing/campaigns/campaign-analytics/${campaignId}/`);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error(`Error fetching Phishing campaign analytics for ID ${campaignId}:`, error);
    return null;
  }
}

/**
 * Generate and download a PDF report for a Phishing campaign
 * @param {string|number} campaignId - The ID of the campaign
 * @param {string} campaignName - The name of the campaign for the downloaded file
 * @returns {Promise<Blob|null>} The PDF blob or null if not available
 */
export async function generatePhishingCampaignReport(campaignId, campaignName) {
  try {
    const response = await getAPI.get(`phishing/report/${campaignId}/`, { 
      responseType: 'arraybuffer' 
    });
    
    if (response.status === 200 && response.data) {
      return new Blob([response.data], { type: 'application/pdf' });
    }
    return null;
  } catch (error) {
    console.error(`Error generating report for Phishing campaign ID ${campaignId}:`, error);
    return null;
  }
}

/**
 * Process campaign details results to group by email and remove duplicates
 * @param {Array} data - The raw campaign data
 * @returns {Object} Processed data grouped by email
 */
export function processPhishingDetailsResults(data) {
  // Step 1: Remove exact duplicates by converting to a Map with JSON string as key
  const uniqueItems = Array.from(new Map(data.map(item => [JSON.stringify(item), item])).values());

  // Step 2: Group by email and sort each group by created_at
  const groupedByEmail = uniqueItems.reduce((groups, item) => {
    const email = item.email;
    if (!groups[email]) {
      groups[email] = [];
    }
    groups[email].push(item);
    return groups;
  }, {});

  // Step 3: Process each group
  for (const email in groupedByEmail) {
    // Step 3a: Remove duplicate statuses within the group
    const seenStatuses = new Set();
    groupedByEmail[email] = groupedByEmail[email].filter(item => {
      if (seenStatuses.has(item.status)) {
        return false; // Duplicate status
      }
      seenStatuses.add(item.status);
      return true;
    });

    // Step 3b: Sort the group by created_at
    groupedByEmail[email].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  }

  return groupedByEmail;
}

/**
 * Update a Phishing campaign
 * @param {string|number} campaignId - The ID of the campaign to update
 * @param {Object} updateData - The data to update
 * @returns {Promise<Object|null>} The updated campaign data or null if failed
 */
export async function updatePhishingCampaign(campaignId, updateData) {
  try {
    const response = await getAPI.put(`smshing/campaign/${campaignId}/`, updateData);
    if (response.status === 200) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error(`Error updating Phishing campaign with ID ${campaignId}:`, error);
    return null;
  }
}